import type {
  AppData,
  AppDataComponent,
  CompRef,
} from '@wix/document-services-types';
import {
  CssCustomProperties,
  CssPropertyItem,
  PanelAction,
} from '@wix/component-protocol/schema';
import { ManifestComponentExtensionType } from '../stores';

import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { BehaviorsPlatformService } from './BehaviorsPlatformService';
import { VirtualManifestAPI } from '../api';
import { VirtualManifestElementType } from '../builders';
import { ApplicationDataPlatformService } from './ApplicationDataPlatformService';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class PanelsPlatformService extends AbstractComponentsPlatformService {
  public readonly applicationDataService: ApplicationDataPlatformService;
  public readonly behaviorsService: BehaviorsPlatformService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.applicationDataService = new ApplicationDataPlatformService(...args);
    this.behaviorsService = new BehaviorsPlatformService(...args);
  }

  #shouldShowBuilderElementsPanel(api: VirtualManifestAPI): boolean {
    const NONE = 'none';

    const isValidDisplayPropertyItem = (
      item: CssPropertyItem | CssCustomProperties[string],
    ) => {
      if (!item) {
        return false;
      }
      const values = item.display?.displayValues;
      return Array.isArray(values) && (values as string[]).includes(NONE);
    };

    const isValidCssGroup = (
      group: Record<string, CssPropertyItem | CssCustomProperties>,
    ) => Object.values(group).some(isValidDisplayPropertyItem);

    const { cssCustomProperties, cssProperties } =
      api.resolver.getStyleDefinitions();

    const isRemovableValue = this.behaviorsService.getValue(
      api.getEditorPointer(),
      'removable',
    );

    const isElementRemovable = isRemovableValue ?? true;

    const isValidRemovableElement =
      isValidCssGroup(cssProperties) && isElementRemovable;

    const children = api
      .getChildren()
      .filter((child) => child.resolver.isElementEditable());

    return (
      isValidRemovableElement ||
      isValidCssGroup(cssCustomProperties) ||
      children.some((child) => this.#shouldShowBuilderElementsPanel(child))
    );
  }

  #getPanelComponent(panelId: string, appData?: AppData | null) {
    return appData?.components?.find(
      ({ componentId }) => componentId === panelId,
    );
  }

  /**
   * TODO: migrate action definitions (manifest + derived) to component service and make this function internal
   */
  getPanelDefinitionByPanelId(
    editorPointer: EditorPointer<CompRef>,
    panelId: string,
  ) {
    const node = this.virtualElementProvider.buildVirtualElement({
      editorPointers: [editorPointer],
    });

    let appData: AppData | null;
    let panelData: AppDataComponent | undefined;

    if (node.isRefElement()) {
      // try "current" app (where ref element is used)
      appData = this.applicationDataService.getAppData(
        node.getRoot().getEditorPointer(),
      );
      panelData = this.#getPanelComponent(panelId, appData);

      if (!panelData) {
        // try "onwer" app (where ref element is defined)
        appData = this.applicationDataService.getAppData(editorPointer);
        panelData = this.#getPanelComponent(panelId, appData);
      }
    } else {
      appData = this.applicationDataService.getAppData(editorPointer);
      panelData = this.#getPanelComponent(panelId, appData);
    }

    return panelData?.data as NonNullable<PanelAction>['inline'] | undefined;
  }

  getPanelContent(editorPointer: EditorPointer<CompRef>, actionName: string) {
    const node = this.virtualElementProvider.buildVirtualElement({
      editorPointers: [editorPointer],
    });
    const virtualManifest = node.getVirtualManifest();

    if (this.panelStore.hasPanelByEditorPointer(editorPointer, actionName)) {
      return this.panelStore.getPanelByEditorPointer(editorPointer, actionName);
    } else if (
      virtualManifest.tree.elementType === VirtualManifestElementType.RefElement
    ) {
      return this.panelStore.getPanelFactory(
        node.getComponentType(),
        actionName,
      )?.(editorPointer);
    }

    return null;
  }

  shouldShowElementsPanel(editorPointer: EditorPointer<CompRef>): boolean {
    const extensionType =
      this.manifestStore.getExtensionTypeByEditorPointer(editorPointer);

    switch (extensionType) {
      case ManifestComponentExtensionType.OOI: {
        const metadata =
          this.manifestStore.getMetadataByEditorPointer<ManifestComponentExtensionType.OOI>(
            editorPointer,
          );

        return !!metadata?.statics?.elementsPanel?.length;
      }

      case ManifestComponentExtensionType.BUILDER: {
        return this.#shouldShowBuilderElementsPanel(
          this.virtualElementProvider
            .buildVirtualElement({
              editorPointers: [editorPointer],
            })
            .getAPI(),
        );
      }

      default: {
        return false;
      }
    }
  }
}
